import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Users, Clock, ArrowLeft, ArrowRight, AlertCircle, Lock, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';
import { useTheme } from '../context/ThemeContext';
import API_URL from '../api';

function parseDateStr(dateStr) {
  if (!dateStr) return null;
  const dmyMatch = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (dmyMatch) return new Date(dmyMatch[3], dmyMatch[2] - 1, dmyMatch[1]);
  return new Date(dateStr);
}

function parseStageTime(stage, eventDate) {
  const dateStr = stage.date || eventDate;
  const timeStr = stage.startTime || (stage.time && stage.time.split(/[-–]/)[0].trim());
  if (!timeStr) return null;
  const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (!match) return null;
  let hours = parseInt(match[1]);
  const minutes = parseInt(match[2]);
  const period = match[3];
  if (period) {
    if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
    if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
  }
  const date = parseDateStr(dateStr);
  date.setHours(hours, minutes, 0, 0);
  return date;
}

function parseEndTime(stage, eventDate) {
  const dateStr = stage.endDate || stage.date || eventDate;
  const timeStr = stage.endTime || (stage.time && stage.time.match(/[-–]\s*(.+)/)?.[1]?.trim());
  if (!timeStr) return null;
  const match = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
  if (!match) return null;
  let hours = parseInt(match[1]);
  const minutes = parseInt(match[2]);
  const period = match[3];
  if (period) {
    if (period.toUpperCase() === 'PM' && hours !== 12) hours += 12;
    if (period.toUpperCase() === 'AM' && hours === 12) hours = 0;
  }
  const date = parseDateStr(dateStr);
  date.setHours(hours, minutes, 0, 0);
  return date;
}

function getStageTimeLabel(stage) {
  if (stage.startTime) {
    return stage.startTime + (stage.endTime ? ' - ' + stage.endTime : '');
  }
  return stage.time || null;
}

function StageItem({ stage, index, total, eventDate, darkMode, text, subtext, border }) {
  const [timeLeft, setTimeLeft] = useState('');
  const [stageStatus, setStageStatus] = useState('upcoming');
  const targetTime = parseStageTime(stage, eventDate);
  const endTime = parseEndTime(stage, eventDate);
  const timeLabel = getStageTimeLabel(stage);

  useEffect(() => {
    if (!targetTime) return;
    const calc = () => {
      const now = new Date();
      if (endTime && now >= targetTime && now <= endTime) {
        setStageStatus('active');
const diff = endTime - now;
const h = Math.floor(diff / 3600000);
const m = Math.floor((diff % 3600000) / 60000);
const s = Math.floor((diff % 60000) / 1000);
setTimeLeft(h > 0 ? `${h}h ${m}m left` : m > 0 ? `${m}m ${s}s left` : `${s}s left`);
      } else if (now > (endTime || targetTime)) {
        setStageStatus('passed');
        setTimeLeft('Completed');
      } else {
        setStageStatus('upcoming');
        const diff = targetTime - now;
        const d = Math.floor(diff / 86400000);
        const h = Math.floor((diff % 86400000) / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        setTimeLeft(d > 0 ? `Starts in ${d}d ${h}h` : h > 0 ? `Starts in ${h}h ${m}m` : m > 0 ? `Starts in ${m}m ${s}s` : `Starts in ${s}s`);
      }
    };
    calc();
    const interval = setInterval(calc, 1000);
    return () => clearInterval(interval);
  }, [stage.startTime, stage.time, stage.date, stage.endDate, eventDate]);

  const statusColors = {
    active: {
      dot: '#4caf50',
      bg: darkMode ? 'rgba(76,175,80,0.08)' : '#f1f8f1',
      border: 'rgba(76,175,80,0.3)',
      badge: { bg: 'rgba(76,175,80,0.15)', color: '#4caf50' }
    },
    passed: {
      dot: '#888',
      bg: darkMode ? 'rgba(255,255,255,0.02)' : '#f8f8f8',
      border: darkMode ? 'rgba(255,255,255,0.05)' : '#eee',
      badge: { bg: 'rgba(136,136,136,0.15)', color: '#888' }
    },
    upcoming: {
      dot: '#e94560',
      bg: darkMode ? 'rgba(233,69,96,0.05)' : '#fff5f7',
      border: 'rgba(233,69,96,0.15)',
      badge: { bg: 'rgba(233,69,96,0.12)', color: '#e94560' }
    },
  };

  const colors = statusColors[stageStatus];

  return (
    <div style={{ display: 'flex', gap: '20px', marginBottom: index < total - 1 ? '20px' : '0' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{
          width: '40px', height: '40px', borderRadius: '50%',
          background: stageStatus === 'active' ? 'linear-gradient(135deg, #4caf50, #388e3c)' :
            stageStatus === 'passed' ? (darkMode ? 'rgba(255,255,255,0.1)' : '#e0e0e0') :
            'linear-gradient(135deg, #e94560, #c62a47)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: stageStatus === 'passed' ? '#888' : '#fff',
          fontWeight: '700', fontSize: '14px', flexShrink: 0, zIndex: 1,
          boxShadow: stageStatus === 'active' ? '0 0 0 4px rgba(76,175,80,0.2)' :
            stageStatus === 'upcoming' ? '0 4px 12px rgba(233,69,96,0.3)' : 'none',
          transition: '0.3s'
        }}>
          {stageStatus === 'passed' ? '✓' : stageStatus === 'active' ? '▶' : index + 1}
        </div>
        {index < total - 1 && (
          <div style={{
            width: '2px', flex: 1, marginTop: '8px',
            background: stageStatus === 'passed' ? '#4caf50' : (darkMode ? 'rgba(255,255,255,0.1)' : '#e0e0e0'),
            minHeight: '20px', transition: '0.5s'
          }} />
        )}
      </div>

      <div style={{
        flex: 1, background: colors.bg, borderRadius: '14px',
        padding: '16px 18px', border: `1px solid ${colors.border}`, transition: '0.3s'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '8px', marginBottom: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {stageStatus === 'active' && (
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#4caf50', display: 'inline-block', animation: 'pulseDot 1.5s infinite' }} />
            )}
            <h3 style={{ color: text, fontSize: '15px', fontWeight: '700', margin: 0 }}>{stage.title}</h3>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
            {timeLabel && (
              <span style={{ background: darkMode ? 'rgba(233,69,96,0.1)' : '#fff5f7', color: '#e94560', padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                🕐 {timeLabel}
              </span>
            )}
            {targetTime && (
              <span style={{ background: colors.badge.bg, color: colors.badge.color, padding: '3px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                {stageStatus === 'active' ? '🟢 LIVE — ' : stageStatus === 'passed' ? '✅ ' : '⏳ '}{timeLeft}
              </span>
            )}
          </div>
        </div>
        {stage.description && (
          <p style={{ color: subtext, fontSize: '13px', margin: 0, lineHeight: '1.6' }}>{stage.description}</p>
        )}
        {stageStatus === 'active' && (
          <div style={{ marginTop: '12px', height: '3px', background: darkMode ? 'rgba(255,255,255,0.1)' : '#e0e0e0', borderRadius: '10px', overflow: 'hidden' }}>
            <div style={{ height: '100%', background: 'linear-gradient(90deg, #4caf50, #81c784)', borderRadius: '10px', animation: 'shimmer 2s linear infinite', backgroundSize: '200% 100%' }} />
          </div>
        )}
      </div>
    </div>
  );
}

function EventDetail() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [event, setEvent] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const bg = darkMode ? '#0f0f1a' : '#f0f2f5';
  const card = darkMode ? '#1a1a2e' : '#fff';
  const border = darkMode ? 'rgba(255,255,255,0.07)' : '#f0f0f0';
  const text = darkMode ? '#e0e0e0' : '#333';
  const subtext = darkMode ? '#888' : '#666';

  useEffect(() => {
    axios.get(`${API_URL}/api/events/${eventId}`)
      .then(res => setEvent(res.data))
      .catch(() => navigate('/'));
  }, [eventId, navigate]);

  if (!event) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh', background: bg }}>
      <div style={{ width: '40px', height: '40px', border: '3px solid #e94560', borderTop: '3px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );

  const seatsLeft = event.totalSeats - event.registeredCount;
  const isFull = seatsLeft <= 0;
  const isDeadlinePassed = event.registrationDeadline && new Date() > new Date(event.registrationDeadline);
  const isNotOpenYet = event.registrationStartDate && new Date() < new Date(event.registrationStartDate);
  const isLocked = event.isLocked;
  const canRegister = !isFull && !isDeadlinePassed && !isLocked && !isNotOpenYet;

  const getStatusInfo = () => {
    if (isLocked) return { color: '#ff4444', bg: darkMode ? 'rgba(255,68,68,0.1)' : '#ffebee', text: '🔒 Registrations Locked by Admin', border: 'rgba(255,68,68,0.2)' };
    if (isNotOpenYet) return { color: '#3f51b5', bg: darkMode ? 'rgba(63,81,181,0.1)' : '#e8eaf6', text: `🕐 Registration opens on ${new Date(event.registrationStartDate).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}`, border: 'rgba(63,81,181,0.2)' };
    if (isDeadlinePassed) return { color: '#ff9800', bg: darkMode ? 'rgba(255,152,0,0.1)' : '#fff8f0', text: '⏰ Registration Deadline Passed', border: 'rgba(255,152,0,0.2)' };
    if (isFull) return { color: '#ff4444', bg: darkMode ? 'rgba(255,68,68,0.1)' : '#ffebee', text: '❌ Event is Full', border: 'rgba(255,68,68,0.2)' };
    return { color: '#4caf50', bg: darkMode ? 'rgba(76,175,80,0.1)' : '#e8f5e9', text: '✅ Registrations Open', border: 'rgba(76,175,80,0.2)' };
  };

  const status = getStatusInfo();

  return (
    <div style={{ background: bg, minHeight: '100vh' }}>
      <style>{`
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulseDot { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.4; transform: scale(1.4); } }
        @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
        .ql-editor { padding: 0 !important; }
        .rich-content h1, .rich-content h2, .rich-content h3 { color: ${text}; margin: 16px 0 8px; }
        .rich-content p { color: ${subtext}; line-height: 1.8; margin-bottom: 10px; font-size: 14px; }
        .rich-content ul, .rich-content ol { color: ${subtext}; padding-left: 20px; margin-bottom: 10px; line-height: 1.8; font-size: 14px; }
        .rich-content strong { color: ${text}; }
        .rich-content a { color: #e94560; }
      `}</style>

      <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)', padding: '50px 20px 40px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px', background: 'rgba(233,69,96,0.08)', borderRadius: '50%' }} />
        <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative' }}>
          <button onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.1)', color: '#ccc', display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '20px', padding: '8px 16px', borderRadius: '8px', fontSize: '13px', border: '1px solid rgba(255,255,255,0.15)' }}>
            <ArrowLeft size={14} /> Back to Events
          </button>
          <div style={{ display: 'inline-block', background: 'rgba(233,69,96,0.2)', border: '1px solid rgba(233,69,96,0.3)', borderRadius: '20px', padding: '5px 15px', fontSize: '12px', color: '#e94560', marginBottom: '15px', fontWeight: '600' }}>
            🎪 COLLEGE EVENT
          </div>
          <h1 style={{ color: '#fff', fontSize: '32px', fontWeight: '800', marginBottom: '15px', lineHeight: '1.2' }}>{event.title}</h1>
          <p style={{ color: '#aaa', fontSize: '15px', lineHeight: '1.6', marginBottom: '20px' }}>{event.description}</p>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {[
              { icon: <Calendar size={13} />, text: new Date(event.date).toLocaleDateString('en-GB') },
              { icon: <MapPin size={13} />, text: event.venue },
              { icon: <Users size={13} />, text: `${seatsLeft} seats left` },
              ...(event.registrationStartDate && new Date() < new Date(event.registrationStartDate) ? [{ icon: <Clock size={13} />, text: `Opens: ${new Date(event.registrationStartDate).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}` }] : []),
              ...(event.registrationDeadline ? [{ icon: <Clock size={13} />, text: `Deadline: ${new Date(event.registrationDeadline).toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}` }] : [])
            ].map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '7px', background: 'rgba(255,255,255,0.08)', padding: '7px 14px', borderRadius: '20px', color: '#ccc', fontSize: '13px' }}>
                <span style={{ color: '#e94560' }}>{item.icon}</span>{item.text}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '30px 20px' }}>
        <div style={{ background: status.bg, border: `1px solid ${status.border}`, borderRadius: '12px', padding: '14px 18px', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          {isLocked && <Lock size={16} color={status.color} />}
          <span style={{ color: status.color, fontWeight: '600', fontSize: '14px' }}>{status.text}</span>
          {event.registrationDeadline && !isDeadlinePassed && (
            <span style={{ marginLeft: 'auto', color: subtext, fontSize: '13px' }}>
              Closes: {new Date(event.registrationDeadline).toLocaleDateString('en-GB')}
            </span>
          )}
        </div>

        {event.details && (
          <div style={{ background: card, borderRadius: '20px', padding: '28px', marginBottom: '25px', border: `1px solid ${border}`, animation: 'fadeInUp 0.4s ease' }}>
            <h2 style={{ color: text, marginBottom: '20px', fontSize: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              📄 Event Details
            </h2>
            <div style={{ position: 'relative', overflow: 'hidden', maxHeight: expanded ? 'none' : '250px', transition: '0.3s' }}>
              <div className="rich-content" dangerouslySetInnerHTML={{ __html: event.details }} />
              {!expanded && (
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '80px', background: darkMode ? 'linear-gradient(transparent, #1a1a2e)' : 'linear-gradient(transparent, #fff)' }} />
              )}
            </div>
            <button onClick={() => setExpanded(!expanded)} style={{ marginTop: '15px', background: darkMode ? 'rgba(233,69,96,0.1)' : '#fff5f7', color: '#e94560', border: '1px solid rgba(233,69,96,0.2)', borderRadius: '10px', padding: '10px 20px', fontSize: '14px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '7px', cursor: 'pointer' }}>
              {expanded ? <><ChevronUp size={16} /> Read Less</> : <><ChevronDown size={16} /> Read More</>}
            </button>
          </div>
        )}

        {event.stages && event.stages.length > 0 && (
          <div style={{ background: card, borderRadius: '20px', padding: '28px', marginBottom: '25px', border: `1px solid ${border}`, animation: 'fadeInUp 0.5s ease' }}>
            <h2 style={{ color: text, marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '20px' }}>
              <Clock color="#e94560" size={22} /> Schedule & Stages
            </h2>
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '20px' }}>
              {[
                { color: '#4caf50', label: '🟢 Live Now' },
                { color: '#e94560', label: '⏳ Upcoming' },
                { color: '#888', label: '✅ Completed' },
              ].map((item, i) => (
                <span key={i} style={{ background: darkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5', color: item.color, padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '600' }}>
                  {item.label}
                </span>
              ))}
            </div>
            <div style={{ position: 'relative' }}>
              {event.stages.map((stage, i) => (
                <StageItem
                  key={i}
                  stage={stage}
                  index={i}
                  total={event.stages.length}
                  eventDate={event.date}
                  darkMode={darkMode}
                  text={text}
                  subtext={subtext}
                  border={border}
                />
              ))}
            </div>
          </div>
        )}

        {event.rules && (
          <div style={{ background: card, borderRadius: '20px', padding: '28px', marginBottom: '25px', border: `1px solid ${border}`, animation: 'fadeInUp 0.6s ease' }}>
            <h2 style={{ color: text, marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '20px' }}>
              <AlertCircle color="#ff9800" size={22} /> Rules & Guidelines
            </h2>
            <div style={{ background: darkMode ? 'rgba(255,152,0,0.06)' : '#fffbf0', border: `1px solid ${darkMode ? 'rgba(255,152,0,0.15)' : '#ffe082'}`, borderRadius: '12px', padding: '18px' }}>
              <p style={{ color: darkMode ? '#ccc' : '#555', fontSize: '14px', lineHeight: '1.9', whiteSpace: 'pre-line', margin: 0 }}>{event.rules}</p>
            </div>
          </div>
        )}

        <div style={{ background: 'linear-gradient(135deg, #1a1a2e, #0f3460)', borderRadius: '20px', padding: '30px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.05)', animation: 'fadeInUp 0.7s ease' }}>
          <h3 style={{ color: '#fff', fontSize: '18px', marginBottom: '8px' }}>
            {canRegister ? 'Ready to join? 🎉' : 'Registration Unavailable'}
          </h3>
          <p style={{ color: '#aaa', fontSize: '14px', marginBottom: '20px' }}>
            {canRegister ? `Only ${seatsLeft} spots remaining — secure yours now!` : status.text}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/')} style={{ background: 'rgba(255,255,255,0.08)', color: '#ccc', padding: '12px 24px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.15)', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '7px' }}>
              <ArrowLeft size={15} /> Browse More
            </button>
            <button
              onClick={() => canRegister && navigate(`/register/${event._id}`)}
              disabled={!canRegister}
              style={{ background: canRegister ? 'linear-gradient(135deg, #e94560, #c62a47)' : '#555', color: '#fff', padding: '12px 28px', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: canRegister ? 'pointer' : 'not-allowed', display: 'flex', alignItems: 'center', gap: '7px', boxShadow: canRegister ? '0 4px 15px rgba(233,69,96,0.3)' : 'none' }}>
              {canRegister ? (<>Register Now <ArrowRight size={15} /></>) : 'Unavailable'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventDetail;